<template>
  <span
    class="message"
    :class="{ message__questionnaire__control: message.isQuestionnaire }"
    :data-group="showGroupMessage"
  >
    <section
      v-if="isUserMessage"
      v-observe-visibility="{
        callback: loadFile,
        once: true
      }"
      class="message__container"
      :class="[
        `message__${privacy}`,
        `message__sender--${sender}`,
        `message__position--${position}`,
        hasReactions && 'message__with__reactions'
      ]"
      :data-highlight="showHighlight"
      :data-last-message="showLastMessage"
      :data-dd-action-name="`channel_message_${sender}`"
      aria-label="message-container"
      @dblclick="showAppVersion"
    >
      <WAvatar
        v-if="showAvatar"
        class="message__avatar"
        size="xsmall"
        :url="messageSender.photoURL"
        :data-dd-action-name="`channel_message_${sender}`"
      />
      <CReactionContainer
        :show-reaction-option="showReactionIcon"
        class="message__reaction__container"
        :reactions="message.reactions"
        :user-id="user.id"
        :reaction-icons="reactionIcons"
        @toggle-reaction="toggleReaction"
      >
        <section class="message__content__container">
          <CMessageContent
            :message="message"
            :next-message="nextMessage"
            :previous-message="previousMessage"
            :component="contentMessage.component"
            :component-props="contentMessage.props"
            :reply-component="replyMessage"
            :on-play-audio="onPlay"
            :message-sender="messageSender"
            :show-time="!shouldGroupMessage"
            :reply-sender="replyToSender"
            @on-internal-redirect="onInternalRedirect"
          >
            <CMessageOptions
              v-if="showMessageOptions"
              :is-from-member="isFromMember(message)"
              :is-from-current-user="isFromCurrentUser"
              :is-private="isPrivate"
              :file-link="fileLink"
              :channel-id="channelId"
              :message="message"
              :channel-person-id="channelMember.channelPersonId"
              :member-name="channelMember.participantName"
              class="message__options__container"
            />
          </CMessageContent>
        </section>
      </CReactionContainer>
    </section>

    <component
      v-bind="contentMessage.props"
      :is="contentMessage.component"
      v-else
      :data-dd-action-name="`channel_message_${sender}`"
    />
  </span>
</template>

<script>
import { WAvatar } from '@alice-health/wonderland-vue'
import { mapGetters, mapState } from 'vuex'

import {
  CMessageAction,
  CMessageParticipant,
  CMessageQuestionnaire,
  CMessageSplitBar,
  CMessageVisualization
} from '@/components/channels/atoms'
import {
  FILE_MESSAGE_TYPES,
  REACTION_MESSAGE_TYPES,
  USER_MESSAGE_TYPES
} from '@/models/channels'
import { getMessageComponent } from '@/store/pillars/channel/message/utils'
import time from '@/utils/time'

import CMessageContent from './CMessageContent.vue'
import CMessageOptions from './CMessageOptions'
import CReactionContainer from './CReactionContainer.vue'

export default {
  components: {
    CMessageAction,
    CMessageParticipant,
    CMessageSplitBar,
    CMessageVisualization,
    CMessageContent,
    CMessageQuestionnaire,
    CReactionContainer,
    CMessageOptions,
    WAvatar
  },
  props: {
    message: { type: Object, default: undefined },
    nextMessage: { type: Object, default: undefined },
    previousMessage: { type: Object, default: undefined },
    channelId: { type: String, default: undefined },
    shouldHighlight: Boolean,
    isPrivate: Boolean,
    onlyView: {
      type: Boolean,
      default: false
    },
    reactionIcons: {
      type: Array,
      default: () => []
    }
  },
  emits: ['onPlay', 'onInternalRedirect'],
  data() {
    return {
      fileLink: '',
      replyToLink: ''
    }
  },
  computed: {
    ...mapState('user', ['user']),
    ...mapGetters('pillars/channel/message', [
      'isFromMember',
      'getMessageSender'
    ]),
    ...mapGetters('pillars/channel', ['channelMember', 'currentChannel']),
    ...mapGetters('pillars/profile', ['memberUserTypeNonMember']),
    isFromCurrentUser() {
      return this.message.userId === this.user.id
    },
    sender() {
      if (this.isFromMember(this.message)) {
        if (this.memberUserTypeNonMember) return 'non-member'
        return 'member'
      }

      if (this.isFromCurrentUser) return 'staff'
      return 'other'
    },
    position() {
      if (this.isFromCurrentUser) return 'right'
      return 'left'
    },
    privacy() {
      return this.isPrivate ? 'private' : 'public'
    },
    messageSender() {
      return this.getMessageSender(this.message)
    },
    replyToSender() {
      if (this.message.replyTo) {
        return this.getMessageSender(this.message.replyTo)
      }
      return {}
    },
    showAvatar() {
      return (
        !this.isFromCurrentUser &&
        !this.shouldGroupMessage &&
        this.messageSender
      )
    },
    isUserMessage() {
      return USER_MESSAGE_TYPES.includes(this.message.type)
    },
    contentMessage() {
      const componentMessage = getMessageComponent(
        this.message,
        this.fileLink,
        this.channelMember,
        this.currentChannel.staffsArray
      )
      return componentMessage
    },
    replyMessage() {
      if (!this.message.replyTo) {
        return {}
      }
      const componentMessage = getMessageComponent(
        this.message.replyTo,
        this.replyToLink
      )
      return componentMessage
    },
    shouldGroupMessage() {
      const currentMessageAndNextMessageHasTime =
        this.message?.createdAt?.seconds && this.nextMessage?.createdAt?.seconds
      const nextMessageIsValid = this.nextMessage?.id

      if (currentMessageAndNextMessageHasTime && nextMessageIsValid) {
        const sameUser = this.message.userId === this.nextMessage.userId
        const sameDate =
          time(this.message.createdAt.seconds) ===
          time(this.nextMessage.createdAt.seconds)
        return sameUser && sameDate
      }

      return false
    },
    hasReactions() {
      return this.message.reactions?.length
    },
    showReactionIcon() {
      return (
        !this.isFromCurrentUser &&
        REACTION_MESSAGE_TYPES.includes(this.message.type) &&
        !this.onlyView
      )
    },
    showMessageOptions() {
      return !this.onlyView && this.channelMember
    },
    showHighlight() {
      return this.shouldHighlight || null
    },
    showGroupMessage() {
      return this.shouldGroupMessage || null
    },
    showLastMessage() {
      return this.message.isLastMessage || null
    }
  },
  methods: {
    isFileVaultMessage(message) {
      return FILE_MESSAGE_TYPES.includes(message.type)
    },
    async loadFile(isVisible) {
      if (!isVisible) {
        return
      }
      const shouldLoadFromFileVault =
        this.isFileVaultMessage(this.message) && !this.fileLink

      if (shouldLoadFromFileVault) {
        this.fileLink = await this.getMediaLink(this.message.content)
      }
      /**
       * Get link for reply message too
       */
      const shouldLoadReplyToFromFileVault =
        this.message.replyTo &&
        this.isFileVaultMessage(this.message.replyTo) &&
        !this.replyToLink

      if (shouldLoadReplyToFromFileVault) {
        this.replyToLink = await this.getMediaLink(this.message.replyTo.content)
      }
    },
    async getMediaLink(fileVaultLink) {
      return await this.$store.dispatch(
        'pillars/channel/fetchFileUrl',
        fileVaultLink
      )
    },
    showAppVersion() {
      if (this.isFromMember(this.message)) {
        // eslint-disable-next-line no-alert
        alert(`Versão do app: ${this.message.appVersion}`)
      }
    },
    onPlay() {
      this.$emit('onPlay', this.message.id)
    },
    onInternalRedirect(data) {
      this.$emit('onInternalRedirect', data)
    },
    toggleReaction(icon) {
      this.$store.dispatch('pillars/channel/message/toggleReaction', {
        messageId: this.message.id,
        icon,
        reactions: this.message.reactions
      })
    }
  }
}
</script>

<style lang="scss">
.staff--extend {
  background-color: var(--message-staff-background);
  color: var(--message-staff-color);
  border: var(--gl-border-width-xs) solid var(--message-staff-border-color);
  --audio-progress-background: var(--gl-color-shades-gray-40);
  --audio-play-color: var(--sys-color-surface-background-brand);
  --audio-playback-color: var(--sys-color-surface-background-brand);
  --audio-timer-color: var(--gl-color-shades-gray-90);
  --audio-border-rate-color: var(--gl-color-shades-gray-20);
  --base-player-color: var(--sys-color-surface-background-brand);
}

.member--extend {
  color: var(--gl-color-shades-white);
  --message-content-info-color: var(--gl-color-shades-gray-10);
}

.message {
  width: 100%;

  a {
    color: currentColor;
  }

  &__container {
    position: relative;
    display: grid;
    align-items: end;
    color: var(--sys-color-content-primary);
    gap: var(--gl-spacing-01) var(--gl-spacing-02);
    padding: 0 var(--gl-spacing-04);
  }

  &__options__container {
    display: none;
  }

  & + & &__container {
    padding-top: var(--gl-spacing-04);
  }

  &[data-group] + & &__container {
    padding-top: var(--gl-spacing-01);
  }

  &[data-group] &__content__container {
    border-bottom-left-radius: var(--gl-border-radius-md);
    border-bottom-right-radius: var(--gl-border-radius-md);
  }

  &__container[data-highlight] &__content__container {
    background-color: var(--gl-color-shades-yellow-60);
    color: var(--gl-color-shades-gray-90);
  }

  &__position--left {
    grid-template-columns: 24px 1fr;
  }

  &__position--left &__content__container {
    border-bottom-left-radius: var(--gl-border-radius-xs);
  }

  &__position--right &__content__container,
  &__position--right &__reaction__container {
    border-bottom-right-radius: var(--gl-border-radius-xs);
    justify-self: flex-end;
  }

  &__position--right &__data {
    text-align: right;
  }

  &__sender--non-member &__content__container {
    --channel-new-message: #d5cfe1;

    background-color: var(--gl-color-shades-violet-70);
    @extend .member--extend;
  }

  &__sender--member &__content__container {
    background-color: var(--gl-color-shades-magenta-70);
    @extend .member--extend;

    *::selection {
      background-color: var(--gl-color-shades-magenta-40);
    }
  }

  &__sender--staff &__content__container {
    @extend .staff--extend;
  }
  &__sender--other &__content__container {
    @extend .staff--extend;
    background-color: var(--message-other-background);
  }

  &__avatar {
    grid-row: 1/3;
  }

  &__content__container,
  &__reaction__container {
    grid-column: 2/3;
  }
  &__reaction__container {
    position: relative;
  }

  &__content__container {
    width: fit-content;
    background-color: var(--gl-color-shades-gray-20);
    border-radius: var(--gl-border-radius-md);

    p {
      &:last-of-type {
        margin: 0;
      }

      &:empty {
        display: none;
      }
    }
    &:hover {
      .message__options__container {
        display: flex;
      }
    }
  }

  &__questionnaire__control {
    --split-bar-background: var(--sys-color-surface-background-default);

    > * {
      background-color: var(--sys-color-surface-background-default);
    }
  }
}
</style>
<style lang="scss" scoped>
.message {
  &__with__reactions {
    padding-bottom: var(--gl-spacing-02);
  }
  &[data-group] &__with__reactions {
    padding-bottom: var(--gl-spacing-06);
  }
  &__position--right :deep(.message__reaction) {
    right: 0;
    left: auto;
  }
}
</style>
